import { useState } from 'react';
import { Check, FileText, ShieldCheck, Send, ClipboardList, Scale, Briefcase } from 'lucide-react';
import ContactFormDialog from './ContactFormDialog';

const selfFilingSteps = [
  {
    title: 'Application Preparation',
    icon: FileText,
    description: 'IP Protection Associate reviews your filing information, identifies the correct trademark class, and prepares your application for submission through the USPTO TEAS system.',
  },
  {
    title: 'Identity Review and Approval',
    icon: ShieldCheck,
    description: 'You complete ID.me verification, then check the attorney-prepared application in detail. Any required edits or changes can be requested before submission.',
  },
  {
    title: 'USPTO Filing',
    icon: Send,
    description: 'After approval, IP Protection Associate submits your application to the USPTO, ensuring every detail is accurate, complete, and ready for official review.',
  },
];

const attorneyLedSteps = [
  {
    title: 'Information Collection',
    icon: ClipboardList,
    description: 'We collect your trademark details, including name, logo (if applicable), and a brief description of your products/services.',
  },
  {
    title: 'Attorney Review & Application Drafting',
    icon: Scale,
    description: 'Our IP attorney reviews your case, determines the appropriate trademark class(es), and prepares a strong, USPTO-compliant application.',
  },
  {
    title: 'Filing & Case Management',
    icon: Briefcase,
    description: 'We file the application on your behalf and handle all procedural steps, including monitoring your application and managing any USPTO correspondence.',
  },
];

const selfFilingPlans = [
  {
    name: 'Essentials',
    price: '$45',
    fee: 'Govt. filing fee not included',
    paymentNotice: 'Simple guided process with expert support at every step to ensure accuracy',
    features: [
      'Case Review',
      'Case Filing',
      'Basic Search',
      'State E-Filing',
      'Trademark Secured',
    ],
  },
  {
    name: 'Enhanced',
    price: '$149',
    fee: 'Govt. filing fee not included',
    paymentNotice: 'Balanced assistance that helps you move forward with clear professional direction',
    features: [
      'Case Review',
      'Case Filing',
      'Direct Hit Search',
      'Trademark Alert',
      'Trademark Secured',
      'High Satisfaction Rate',
      'Refusal Risk Meter',
    ],
  },
  {
    name: 'Deluxe',
    price: '$299',
    fee: 'Govt. filing fee not included',
    recommended: true,
    paymentNotice: 'Advanced support with deeper planning, careful review, and ongoing clarity',
    features: [
      'Case Review',
      'Case Filing',
      'Direct Hit Search',
      'Trademark Alert',
      'Trademark Secured',
      'High Satisfaction Rate',
      'Refusal Risk Meter',
      'Trademark Monitoring',
      'Complete Documentation',
      'Digital File',
      'Dedicated Case Manager',
    ],
  },
  {
    name: 'Platinum',
    price: '$349',
    fee: 'Govt. filing fee not included',
    paymentNotice: 'Premium guided service with expert attention, leadership, and complete confidence',
    features: [
      'Case Review',
      'Case Filing',
      'Direct Hit Search',
      'Trademark Alert',
      'Trademark Secured',
      'High Satisfaction Rate',
      'Refusal Risk Meter',
      'Trademark Monitoring',
      'Complete Documentation',
      'Digital File',
      'Dedicated Case Manager',
      'High Approval Rate',
      'Comprehensive Search Report',
      'High Priority Service',
      'Cease & Desist Letter',
    ],
  },
];

const attorneyLedPlans = [
  {
    name: 'Essentials',
    price: '$795',
    fee: 'Govt. filing fee not included',
    paymentNotice: 'Reliable professional support for a smoother and more confident filing process',
    features: [
      'Attorney-Handled Trademark Filing from Start to Finish',
      'Professional Case Evaluation',
      'Application Preparation & Submission',
      'Preliminary Trademark Search',
      'State-Level E-Filing Included',
      'Attorney-Handled Trademark Filing from Start to Finish',
      'Professional Case Evaluation',
      'Application Preparation & Submission',
      'Preliminary Trademark Search',
      'State-Level E-Filing Included',
    ],
  },
  {
    name: 'Enhanced',
    price: '$895',
    fee: 'Govt. filing fee not included',
    recommended: true,
    paymentNotice: 'Guided step-by-step assistance with added care, clarity, and peace of mind.',
    features: [
      'Attorney-Led Trademark Filing',
      'End-to-End Case Handling',
      'Comprehensive Case Review & Analysis',
      'Professional Application Preparation',
      'Trademark Secured',
      'USPTO-Compliant Filing Process',
      'Attorney-Led Trademark Filing',
      'End-to-End Case Handling',
      'Comprehensive Case Review & Analysis',
    ],
  },
  {
    name: 'Deluxe',
    price: '$1,145',
    fee: 'Govt. filing fee not included',
    paymentNotice: 'Organized service with expert review, careful preparation, and steady progress',
    features: [
      'Attorney-Led Case Review',
      'Complete Case Filing & Management',
      'Advanced Direct-Hit Trademark Search',
      'Trademark Monitoring & Alerts',
      'Comprehensive Risk & Refusal Meter',
      'Attorney-Led Case Review',
      'Complete Case Filing & Management',
      'Advanced Direct-Hit Trademark Search',
      'Trademark Monitoring & Alerts',
      'Comprehensive Risk & Refusal Meter',
    ],
  },
  {
    name: 'Platinum',
    price: '$1,545',
    fee: 'Govt. filing fee included',
    recommended: true,
    paymentNotice: 'Priority support with focused attention, quicker handling, and greater confidence',
    features: [
      'Attorney-Led Case Review & Strategy',
      'Complete Case Filing & Management',
      'Advanced Direct-Hit Trademark Search',
      'Federal & State Trademark Search Report',
      'Trademark Monitoring & Real-Time Alerts',
      'Attorney-Led Case Review & Strategy',
      'Complete Case Filing & Management',
      'Advanced Direct-Hit Trademark Search',
      'Federal & State Trademark Search Report',
      'Trademark Monitoring & Real-Time Alerts',
    ],
  },
];

const DEFAULT_PACKAGE = { name: 'Essentials', price: '$45' };

export const PricingTabs = () => {
  const [activeTab, setActiveTab] = useState('self-filing');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const tmSignUpType = activeTab === 'self-filing' ? 1 : 2;

  const activePlans = activeTab === 'self-filing' ? selfFilingPlans : attorneyLedPlans;
  const steps = activeTab === 'self-filing' ? selfFilingSteps : attorneyLedSteps;
  const sectionTitle = activeTab === 'self-filing'
    ? 'Self-Filing with Expert Assistance'
    : 'Attorney-Led Filing';
  const sectionSubtitle = activeTab === 'self-filing'
    ? 'Complete Support Plan for Easy Filing'
    : 'Complete End-to-End Service Plan';

  const effectivePackage = selectedPackage || DEFAULT_PACKAGE;

  const handleSelectPlan = (plan) => {
    const pkg = { name: plan.name, price: plan.price };
    setSelectedPackage(pkg);

    console.log({
      tmSignUpType,
      packageTitle: plan.name,
      packageAmount: plan.price,
    });

    setShowDialog(true);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedPackage(null);
  };

  return (
    <>
      <div className="section bg-[#FFF5E6]" data-testid="pricing-tabs-section">
        <div className="container mx-auto">
          {/* Intro Section */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Brand Protection Made Simple
            </h2>
            <p className="text-lg text-[#2C1810] max-w-4xl mx-auto leading-relaxed">
              IP Protection Associate helps businesses secure their identity through reliable filing, monitoring, renewals, and complete trademark support. Whether your goal is to protect a company name, claim ownership of a logo, or safeguard a slogan, our team makes the process clear and manageable. We assist brands across all 50 U.S. states and international markets, helping keep your intellectual property protected wherever your business operates.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-12" role="tablist" aria-label="Filing type">
            <div className="inline-flex bg-white rounded-full p-1 shadow-lg">
              <button
                role="tab"
                aria-selected={activeTab === 'self-filing'}
                aria-controls="tab-panel-self-filing"
                id="tab-self-filing"
                onClick={() => handleTabChange('self-filing')}
                onKeyDown={(e) => e.key === 'ArrowRight' && handleTabChange('attorney-led')}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06214c] ${
                  activeTab === 'self-filing'
                    ? 'bg-[#06214c] text-white shadow-md'
                    : 'text-[#2C1810] hover:bg-[#FFF5E6]'
                }`}
                data-testid="tab-self-filing"
              >
                Self Filing with Assistance
              </button>
              <button
                role="tab"
                aria-selected={activeTab === 'attorney-led'}
                aria-controls="tab-panel-attorney-led"
                id="tab-attorney-led"
                onClick={() => handleTabChange('attorney-led')}
                onKeyDown={(e) => e.key === 'ArrowLeft' && handleTabChange('self-filing')}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06214c] ${
                  activeTab === 'attorney-led'
                    ? 'bg-[#06214c] text-white shadow-md'
                    : 'text-[#2C1810] hover:bg-[#FFF5E6]'
                }`}
                data-testid="tab-attorney-led"
              >
                IP Attorney-Led Filing
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div
            role="tabpanel"
            id={`tab-panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
            className="transition-opacity duration-300"
          >
            {/* Step Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div
                    key={`${activeTab}-step-${index}`}
                    className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
                    data-testid={`step-card-${index}`}
                  >
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center">
                        <Icon className="text-white" size={32} />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-[#06214c] mb-3 text-center" style={{ fontFamily: 'Playfair Display' }}>
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-center">{step.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Pricing Section Heading */}
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                {sectionTitle}
                <br />
                {sectionSubtitle}
              </h3>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {activePlans.map((plan, index) => (
                <div
                  key={`${activeTab}-plan-${index}`}
                  className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                    plan.recommended ? 'ring-4 ring-[#06214c]' : ''
                  }`}
                  data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
                >
                  {plan.recommended && (
                    <div className="absolute top-0 right-0 bg-[#06214c] text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                      Recommended
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-[#06214c] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                      {plan.name}
                    </h3>
                    <div className="mb-2">
                      <div className="text-3xl font-bold text-[#2C1810]">{plan.price}</div>
                      <div className="text-sm text-gray-600">+ {plan.fee}</div>
                    </div>
                    <div className="text-xs text-[#bf8a60] italic mb-4 min-h-[2rem]">{plan.paymentNotice}</div>
                    <div className="h-64 overflow-y-auto mb-4 space-y-2">
                      {plan.features.map((feature, fIndex) => (
                        <div key={fIndex} className="flex items-start space-x-2">
                          <Check className="text-[#06214c] flex-shrink-0 mt-1" size={16} />
                          <span className="text-sm text-[#2C1810]">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mb-4">{plan.note}</div>
                    <button
                      onClick={() => handleSelectPlan(plan)}
                      className="w-full btn-primary"
                      data-testid={`select-plan-${plan.name.toLowerCase()}`}
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ContactFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        source="pricing"
        packageTitle={effectivePackage.name}
        packageAmount={parseInt(effectivePackage.price.replace(/\D/g, ''), 10) || 45}
        tmSignUpType={tmSignUpType}
      />
    </>
  );
};

export default PricingTabs;
